import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { applicationActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { ApplicationApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import { RefreshEvents } from '../../../common/SharedEvents';
import AppraiserModal from '../../../appraisals/appraisers/modals/AppraiserModal';
import { LoadPanel } from 'devextreme-react/load-panel';
import Tabs, { Item } from 'devextreme-react/tabs';
import { PropertyStatusType, PropertyStatusTypeOptions } from '../ApplicationConstants';
import { ModalSummaryHeader } from '../ApplicationComponents';
import { handleAdminError, handleAdminSuccess } from '../../../common/SharedFunctions';
import { LoanType } from '../../../loans/common/LoanConstants';

enum RollbackType {
    EntireApplication = 0,
    IndividualProperties = 1,
}
const rollbackTypes = [
    { id: RollbackType.EntireApplication, name: "Entire Application to 'Submitted' Status" },
    { id: RollbackType.IndividualProperties, name: 'Individual Properties' },
];

const RollbackModal = () => {
    const { isOpen, id, propertyId = 0 } = useSelector((state) => state.application.rollbackModal);
    const { dispatch } = store;

    const [application, set_application] = useState(null);
    const [rollbackType, set_rollbackType] = useState(rollbackTypes[0].id);
    const [properties, set_properties] = useState([]);
    const [property, set_property] = useState(null);

    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    const [tabIndex, set_tabIndex] = useState(-1);

    const applicationApi = new ApplicationApi.ApplicationApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: {
            marginRight: '10px',
            marginLeft: '10px',
            textAlign: 'right',
        },
        form: { paddingTop: '10px' },
        formRow: { marginBottom: '0' },
        nonEditable: {
            backgroundColor: '#f7f7f7',
            padding: '5px 10px',
            margin: '5px',
        },
        noPaddingRight: { paddingLeft: '0' },
        padding10Bottom: { marginBottom: '10px' },
        sectionHeader: { marginLeft: '0' },
        option: { paddingBottom: '10px' },
        optionHeader: { marginLeft: '10px' },
        disabledOptionHeader: { marginLeft: '10px', color: 'silver' },
    };

    useEffect(() => {
        async function isOpenChanged() {
            resetState();

            if (!isOpen || !id) {
                return;
            }

            await loadApplication(id);
        }

        isOpenChanged();
    }, [isOpen, id]);

    useEffect(() => {
        if (!properties) {
            return;
        }

        if (!(tabIndex >= 0)) {
            const firstActiveIndex = properties.findIndex((p) => !p.disabled);
            set_tabIndex(firstActiveIndex);
        }
    }, [properties]);

    useEffect(() => {
        if (!properties || properties.length === 0) {
            set_property(null);
            return;
        }
        if (tabIndex < 0) {
            return;
        }
        set_property(properties[tabIndex]);
    }, [tabIndex]);

    const resetState = () => {
        set_rollbackType(rollbackTypes[0].id);
        set_tabIndex(-1);
        set_application(null);
        set_properties([]);
        set_property(null);
        set_loadingOpen(false);
    };

    const loadApplication = async (id) => {
        try {
            set_loadingMessage('Loading...');
            set_loadingOpen(true);

            const response = await applicationApi.getAppraisalRollback(id);
            if (response.statusCode !== 200) {
                set_loadingOpen(false);
                return;
            }

            const appl = response.result;
            set_application(appl);
            appl.properties.forEach((p) => {
                if (propertyId && p.id !== propertyId) {
                    p.disabled = true;
                }
            });

            set_properties(appl.properties);

            set_loadingOpen(false);
        } catch (ex) {
            set_loadingOpen(false);
        }
    };

    const _handleClose = () =>
        dispatch(
            applicationActions.setRollbackModal({
                rollbackModal: { isOpen: false },
            })
        );

    const canSubmit = () => {
        return (
            rollbackType === rollbackTypes[0].id ||
            properties.filter((p) => !p.disabled).some((p) => p.newStatusId >= PropertyStatusType.Pending)
        );
    };

    const submitChanges = async (e) => {
        e.preventDefault();

        try {
            set_loadingMessage('Saving...');
            set_loadingOpen(true);

            const model = {
                id: application.id,
                rollbackType,
                properties: [],
            };

            if (rollbackType === rollbackTypes[1].id) {
                properties
                    .filter((p) => !p.disabled && p.newStatusId >= PropertyStatusType.Pending)
                    .forEach((p) =>
                        model.properties.push({
                            id: p.id,
                            statusId: p.newStatusId,
                        })
                    );
            }

            const response = await applicationApi.setAppraisalRollback(application.id, model);
            if (response.statusCode !== 200) {
                set_loadingOpen(false);
                return;
            }

            refreshEvents.dispatchRefreshEvent();

            handleAdminSuccess('Changes saved successfully.');
            set_loadingOpen(false);

            setTimeout(() => {
                _handleClose();
            }, 600);
        } catch (ex) {
            set_loadingOpen(false);
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        }
    };

    const onTabsSelectionChanged = (args) => {
        if (args.name === 'selectedIndex') {
            let ti = args.value;
            if (args.value >= 0) {
                var prop = properties[ti];
                if (prop.disabled) {
                    ti = properties.findIndex((p) => !p.disabled);
                }
            }
            set_tabIndex(ti);
        }
    };

    const setPropertyStatusId = (id, statusId) => {
        var property = properties.find((p) => p.id === id);
        if (!property) {
            return;
        }

        property.newStatusId = statusId;
        var newProperties = properties.map((p) => (p.id !== property.id ? p : property));
        set_properties(newProperties);
    };

    const rollbackOptions = (i: { id: number; name: string }) => {
        return (
            <div style={styles.option}>
                <input
                    key={'type_' + i.id}
                    type='radio'
                    name='appStatus'
                    value={i.id}
                    defaultChecked={rollbackType === i.id}
                    onClick={(e) => set_rollbackType(parseInt(e.target.value))}
                />
                <span style={styles.optionHeader}>{i.name}</span>
            </div>
        );
    };

    if (!isOpen || !application) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex='-1'
                            role='dialog'
                            style={{ width: '680px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <form
                                    name='rollbackForm'
                                    onSubmit={(event) => submitChanges(event)}
                                >
                                    <div
                                        className='page-header'
                                        role='banner'
                                    >
                                        <h1 style={{ whiteSpace: 'nowrap' }}>Rollback</h1>
                                        <button
                                            type='button'
                                            className='close'
                                            data-dismiss='modal'
                                            aria-label='Close'
                                            onClick={_handleClose}
                                        >
                                            <span aria-hidden='true'>&times;</span>
                                        </button>
                                    </div>

                                    <ModalSummaryHeader application={application} />

                                    <div
                                        className='scrollable-content'
                                        style={{ margin: '10px' }}
                                    >
                                        <div
                                            style={styles.form}
                                            className={'standard-form'}
                                        >
                                            <h2 className='decorated'>Rollback Type</h2>
                                            {rollbackTypes.map((i) => rollbackOptions(i))}

                                            {rollbackType === rollbackTypes[RollbackType.IndividualProperties].id && (
                                                <>
                                                    <h2 className='decorated'>
                                                        {application.properties.length > 1 ? 'Properties' : 'Property'}
                                                    </h2>
                                                    <div>
                                                        {application.isPortfolio && (
                                                            <div
                                                                style={{
                                                                    marginRight: '5px',
                                                                }}
                                                            >
                                                                <Tabs
                                                                    showNavButtons={true}
                                                                    width={'80%'}
                                                                    selectedIndex={tabIndex}
                                                                    onOptionChanged={onTabsSelectionChanged}
                                                                >
                                                                    {application.properties.map((p) => {
                                                                        return (
                                                                            <Item
                                                                                text={'Prop ' + p.bfiNumberSuffix}
                                                                                disabled={p.disabled}
                                                                            />
                                                                        );
                                                                    })}
                                                                </Tabs>
                                                            </div>
                                                        )}
                                                        {property && (
                                                            <div
                                                                style={{
                                                                    border: '1px solid silver',
                                                                    padding: '5px',
                                                                    marginBottom: '10px',
                                                                    marginRight: '5px',
                                                                }}
                                                            >
                                                                <div className='row'>
                                                                    <div
                                                                        className='columns small-12 large-12'
                                                                        style={styles.nonEditable}
                                                                    >
                                                                        {property.description && (
                                                                            <div>
                                                                                <b>
                                                                                    Property {property.bfiNumberSuffix} -{' '}
                                                                                    {property.description}
                                                                                </b>
                                                                            </div>
                                                                        )}
                                                                        {property.address && (
                                                                            <div
                                                                                style={{
                                                                                    marginTop: '5px',
                                                                                    fontWeight: 'bold',
                                                                                }}
                                                                            >
                                                                                {property.address?.street1}{' '}
                                                                                {property.address?.street2}{' '}
                                                                                {property.address?.city},{' '}
                                                                                {property.address?.state}{' '}
                                                                                {property.address?.zipCode}
                                                                            </div>
                                                                        )}
                                                                        {property.statusId >= 0 && (
                                                                            <div
                                                                                style={{
                                                                                    marginTop: '5px',
                                                                                    marginBottom: '10px',
                                                                                }}
                                                                            >
                                                                                Status:{' '}
                                                                                <b>
                                                                                    {
                                                                                        PropertyStatusTypeOptions.find(
                                                                                            (p) => p.id === property.statusId
                                                                                        ).name
                                                                                    }
                                                                                </b>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div
                                                                    className='row'
                                                                    style={styles.formRow}
                                                                >
                                                                    <div className='columns small-12 large-12'>
                                                                        <h6 style={styles.sectionHeader}>Appraise</h6>
                                                                        <div style={styles.option}>
                                                                            <input
                                                                                type='radio'
                                                                                name='propStatus'
                                                                                value={PropertyStatusType.Appraise}
                                                                                defaultChecked={
                                                                                    property.newStatusId ===
                                                                                    PropertyStatusType.Appraise
                                                                                }
                                                                                disabled={!property?.appraise_BfiToOrder}
                                                                                onClick={(e) =>
                                                                                    setPropertyStatusId(
                                                                                        property.id,
                                                                                        parseInt(e.target.value)
                                                                                    )
                                                                                }
                                                                            />
                                                                            <span
                                                                                style={
                                                                                    property?.appraise_BfiToOrder
                                                                                        ? styles.optionHeader
                                                                                        : styles.disabledOptionHeader
                                                                                }
                                                                            >
                                                                                BFI to Order
                                                                            </span>
                                                                        </div>
                                                                        <div style={styles.option}>
                                                                            <input
                                                                                type='radio'
                                                                                name='status'
                                                                                value={PropertyStatusType.AppraisalOrdered}
                                                                                defaultChecked={
                                                                                    property.newStatusId ===
                                                                                    PropertyStatusType.AppraisalOrdered
                                                                                }
                                                                                disabled={!property?.appraise_Ordered}
                                                                                onClick={(e) =>
                                                                                    setPropertyStatusId(
                                                                                        property.id,
                                                                                        parseInt(e.target.value)
                                                                                    )
                                                                                }
                                                                            />
                                                                            <span
                                                                                style={
                                                                                    property?.appraise_Ordered
                                                                                        ? styles.optionHeader
                                                                                        : styles.disabledOptionHeader
                                                                                }
                                                                            >
                                                                                Ordered
                                                                            </span>
                                                                        </div>
                                                                        <div style={styles.option}>
                                                                            <input
                                                                                type='radio'
                                                                                name='status'
                                                                                value={PropertyStatusType.AppraisalReceived}
                                                                                defaultChecked={
                                                                                    property.newStatusId ===
                                                                                    PropertyStatusType.AppraisalReceived
                                                                                }
                                                                                disabled={!property?.appraise_Received}
                                                                                onClick={(e) =>
                                                                                    setPropertyStatusId(
                                                                                        property.id,
                                                                                        parseInt(e.target.value)
                                                                                    )
                                                                                }
                                                                            />
                                                                            <span
                                                                                style={
                                                                                    property?.appraise_Received
                                                                                        ? styles.optionHeader
                                                                                        : styles.disabledOptionHeader
                                                                                }
                                                                            >
                                                                                Received
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div
                                        className='button-footer'
                                        style={styles.buttonContainer}
                                    >
                                        <button
                                            style={styles.buttonMarginRight}
                                            type='submit'
                                            className='button primary'
                                            disabled={!canSubmit()}
                                        >
                                            Submit
                                        </button>
                                        <button
                                            type='button'
                                            className='button hollow'
                                            data-dismiss='modal'
                                            onClick={_handleClose}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
            <AppraiserModal />
        </>
    );
};

export default RollbackModal;
